import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  Link,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import DialogWrapper from './DialogWrapper';
import { submitFeedback } from '@/api/feedback';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  feedbackType: 'positive' | 'negative';
  messageId: string;
  conversationId: string;
  onSubmit: (success: boolean, message: string) => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  feedbackType,
  messageId,
  conversationId,
  onSubmit
}) => {
  const [comments, setComments] = useState('');
  const [includeConversation, setIncludeConversation] = useState(true);
  const [allowContact, setAllowContact] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      await submitFeedback({
        message_id: messageId,
        conversation_id: conversationId,
        feedback_type: feedbackType,
        comments: comments.trim() || undefined,
        include_conversation: includeConversation,
        allow_contact: allowContact
      });
      
      onSubmit(true, 'Your feedback will be shared with Bolt Core Team. Thank you!');
      handleClose();
    } catch (error: any) {
      let errorMessage = 'Unable to submit feedback. Please try again later.';
      
      if (error.response?.status === 409) {
        errorMessage = 'Feedback already submitted for this message';
      } else if (error.response?.data?.detail) {
        // Use the detailed error message from backend
        errorMessage = error.response.data.detail;
      }
      
      onSubmit(false, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setComments('');
      setIncludeConversation(true);
      setAllowContact(true);
      onClose();
    }
  };

  const dialogTitle = feedbackType === 'positive' 
    ? 'What did you like:' 
    : 'What did you dislike:';

  return (
    <DialogWrapper 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Submit feedback to Bolt Core Team
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
          {dialogTitle}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Do not include any private or sensitive information."
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeConversation}
                onChange={(e) => setIncludeConversation(e.target.checked)}
                color="primary"
              />
            }
            label={
              includeConversation
                ? "Include all previous messages from this conversation"
                : "Only send this response and related query"
            }
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={allowContact}
                onChange={(e) => setAllowContact(e.target.checked)}
                color="primary"
              />
            }
            label={
              allowContact
                ? "Bolt Core Team can contact me"
                : "Bolt Core Team should not contact me"
            }
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          <Link 
            href="https://bosnet.sharepoint.com/:w:/r/sites/Bossard_AI/AI%20related%20Documents/Legal%20Notice%20and%20Disclaimer.docx?d=wdddf2b40d49f45d586050b9cecb92384&csf=1&web=1&e=4bdBDz"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            Learn more
          </Link>
          {' '}about how this data is used and your rights. By pressing Submit, your feedback will be sent to the Bolt Core Team and used to improve our product and service.{' '}
          <Link
            href="https://www.bossard.com/global-en/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            Privacy statement
          </Link>
          .
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="contained"
          color="primary"
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
};

export default FeedbackDialog;
